/**
 * ContractSignings.jsx – Vertragsunterschriften Übersicht (Admin)
 * Zeigt alle Verträge, Status, ermöglicht Admin-Gegenzeichnung + PDF-Download.
 */
import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import SignatureCanvas from 'react-signature-canvas';
import SignaturePad from '../../components/ui/SignaturePad';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, CheckCircle2, Clock, Loader2, FileDown, Edit2,
  ChevronDown, ChevronUp, User, RefreshCw, Shield,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  draft:     { label: 'Entwurf', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  signed_an: { label: 'AN unterschrieben – warte auf Admin', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  signed_ag: { label: 'Admin unterschrieben', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  complete:  { label: 'Vollständig unterzeichnet', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
};

export default function ContractSignings() {
  const [searchParams] = useSearchParams();
  const [signings, setSignings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [signModal, setSignModal] = useState(null); // signing object
  const [signOrt, setSignOrt] = useState('Fellbach');
  const [signDatum, setSignDatum] = useState(new Date().toISOString().slice(0, 10));
  const [signing, setSigning] = useState(false);
  const sigRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => { fetchSignings(); }, []);

  // Deep-Link: ?signing={id} → Auto-expand + ggf. Gegenzeichnen-Modal öffnen
  useEffect(() => {
    const targetId = searchParams.get('signing');
    if (!targetId || signings.length === 0) return;
    const target = signings.find(s => s.id === targetId);
    if (!target) return;
    setExpanded(targetId);
    if (target.status === 'signed_an') {
      setSignModal(target);
    }
    // Zur Karte scrollen
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="signing-${targetId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }, [signings, searchParams]);

  const fetchSignings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contracts/signing`, { headers });
      setSignings(r.data || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const handleDownloadPDF = async (s) => {
    try {
      const res = await axios.get(`${API}/contracts/signing/${s.id}/pdf`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const name = s.variables_used?.vorname || 'Mitarbeiter';
      const a = document.createElement('a'); a.href = url;
      a.download = `Arbeitsvertrag_${name}_${s.variables_used?.nachname || ''}.pdf`; a.click();
    } catch { toast.error('PDF-Fehler'); }
  };

  const handleSignAG = async () => {
    if (!signModal) return;
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Bitte unterschreibe im Feld'); return;
    }
    setSigning(true);
    try {
      await axios.post(`${API}/contracts/signing/${signModal.id}/sign-ag`, {
        signature_ag: sigRef.current.toDataURL('image/png'),
        datum: new Date(signDatum).toLocaleDateString('de-DE'),
        ort: signOrt,
      }, { headers });
      toast.success('Vertrag gegengezeichnet!');
      setSignModal(null);
      fetchSignings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  const [, setSaving] = useState(false);

  const formatDate = (iso) => {
    if (!iso) return '–';
    try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso.slice(0, 10); }
  };

  const needsCounter = signings.filter(s => s.status === 'signed_an').length;

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Verträge & Unterschriften
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Alle Arbeitsverträge im Überblick – digital unterzeichnen und archivieren
            </p>
          </div>
          {needsCounter > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
              <Clock className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
                {needsCounter} Vertrag{needsCounter !== 1 ? 'e' : ''} warte{needsCounter === 1 ? 't' : 'n'} auf Gegenzeichnung
              </p>
            </div>
          )}
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : signings.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center space-y-3">
              <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto" />
              <p className="text-muted-foreground font-medium">Noch keine Verträge</p>
              <p className="text-sm text-muted-foreground/70">
                Weise beim Onboarding eines Mitarbeiters eine Vertragsvorlage zu.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {signings.map(s => {
              const vars = s.variables_used || {};
              const displayName = `${vars.vorname || ''} ${vars.nachname || ''}`.trim() || 'Mitarbeiter';
              const sCfg = STATUS_CFG[s.status] || STATUS_CFG.draft;
              const isExp = expanded === s.id;

              return (
                <Card key={s.id} className="shadow-sm" data-testid={`signing-${s.id}`}>
                  <CardContent className="p-0">
                    <button className="w-full p-4 text-left" onClick={() => setExpanded(isExp ? null : s.id)}>
                      <div className="flex items-center gap-3">
                        <div className="bg-primary/10 rounded-xl w-10 h-10 flex items-center justify-center shrink-0">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground">{displayName}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sCfg.cls}`}>{sCfg.label}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {s.template_name} · Erstellt: {formatDate(s.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {s.status === 'signed_an' && (
                            <Button size="sm" onClick={e => { e.stopPropagation(); setSignModal(s); }}
                              className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs">
                              <Edit2 className="h-3.5 w-3.5" /> Gegenzeichnen
                            </Button>
                          )}
                          {(s.status === 'signed_ag' || s.status === 'complete') && (
                            <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); handleDownloadPDF(s); }}
                              className="gap-1.5 text-xs">
                              <FileDown className="h-3.5 w-3.5" /> PDF
                            </Button>
                          )}
                          {isExp ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </div>
                      </div>
                    </button>

                    {isExp && (
                      <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                        {/* Vertragstext Vorschau */}
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Vertragstext (Snapshot)</p>
                          <div className="bg-muted/40 rounded-lg p-3 max-h-40 overflow-y-auto">
                            <pre className="text-xs text-foreground whitespace-pre-wrap font-sans leading-relaxed">
                              {(s.text_snapshot || '').slice(0, 500)}...
                            </pre>
                          </div>
                        </div>

                        {/* Unterschriften-Status */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className={`rounded-lg p-3 ${s.signature_an ? 'bg-green-50 dark:bg-green-900/20 border border-green-200' : 'bg-muted/40'}`}>
                            <p className="text-xs font-medium flex items-center gap-1.5">
                              <User className="h-3.5 w-3.5" />
                              Arbeitnehmer
                              {s.signature_an ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Clock className="h-3.5 w-3.5 text-muted-foreground" />}
                            </p>
                            {s.signed_at_an ? (
                              <p className="text-xs text-muted-foreground mt-1">{formatDate(s.signed_at_an)}</p>
                            ) : <p className="text-xs text-muted-foreground mt-1">Ausstehend</p>}
                          </div>
                          <div className={`rounded-lg p-3 ${s.signature_ag ? 'bg-green-50 dark:bg-green-900/20 border border-green-200' : 'bg-muted/40'}`}>
                            <p className="text-xs font-medium flex items-center gap-1.5">
                              <Shield className="h-3.5 w-3.5" />
                              Arbeitgeber
                              {s.signature_ag ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Clock className="h-3.5 w-3.5 text-muted-foreground" />}
                            </p>
                            {s.signed_at_ag ? (
                              <p className="text-xs text-muted-foreground mt-1">{formatDate(s.signed_at_ag)}</p>
                            ) : <p className="text-xs text-muted-foreground mt-1">Ausstehend</p>}
                          </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(s)} className="gap-1.5 text-xs">
                            <FileDown className="h-3.5 w-3.5" /> PDF herunterladen
                          </Button>
                          {s.status === 'signed_an' && (
                            <Button size="sm" onClick={() => setSignModal(s)}
                              className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs">
                              <Edit2 className="h-3.5 w-3.5" /> Jetzt gegenzeichnen
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Admin-Gegenzeichnung Modal */}
      <Dialog open={!!signModal} onOpenChange={() => setSignModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-amber-600" />
              Vertrag gegenzeichnen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Arbeitnehmer hat den Vertrag bereits unterschrieben. Bitte prüfe den Vertrag und zeichne gegenzeichne.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Ort</Label>
                <Input value={signOrt} onChange={e => setSignOrt(e.target.value)} placeholder="z.B. Fellbach" className="h-9" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Datum</Label>
                <input type="date" value={signDatum} onChange={e => setSignDatum(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Ihre Unterschrift *</Label>
              <div className="border-2 border-border rounded-xl overflow-hidden bg-white dark:bg-gray-900">
                <SignaturePad ref={sigRef} penColor="#1E40AF" height={180} />
              </div>
              <button type="button" onClick={() => sigRef.current?.clear()}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                <RefreshCw className="h-3.5 w-3.5" /> Zurücksetzen
              </button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSignModal(null)}>Abbrechen</Button>
            <Button onClick={handleSignAG} disabled={signing}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-2">
              {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Gegenzeichnen & abschließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
